import React, { useContext } from 'react';
import i18next from 'core/i18n';
import { DialogContent, useFlashMessage } from '@indigo-ag/component-module';
import { AgFinancingFile } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';

import { EnhancedDialog, ModalActionButtons } from 'shared';
import { updateAgFinancingMatricula, getAgFinancingMatriculas } from 'features/brazil-grower/queries';
import { FormattedMatriculasDetailData, MatriculaContext } from '../MatriculasList';
import { removeFilesFromAgFinancing } from '../../../../utils';

interface DeleteFileModalProps {
  closeModal: () => void;
  open: boolean;
  isFieldMonitoring: boolean;
  selectedFile?: AgFinancingFile;
  selectedMatricula?: FormattedMatriculasDetailData;
}

export const DeleteFileModal: React.FunctionComponent<DeleteFileModalProps> = ({
  closeModal,
  open,
  isFieldMonitoring,
  selectedFile,
  selectedMatricula
}) => {
  /**
   * Mutations
   */
  const [updateAgFinancingMatriculaFn] = useMutation(updateAgFinancingMatricula);

  /**
   * MatriculaContext
   */
  const { agFinancingId } = useContext(MatriculaContext);

  const { addFlashMessage } = useFlashMessage();

  const deleteFile = () => {
    return updateAgFinancingMatriculaFn({
      refetchQueries: [{ query: getAgFinancingMatriculas, variables: { agFinancingId } }],
      variables: {
        agFinancingId,
        matricula: {
          id: selectedMatricula?.id,
          kml: !isFieldMonitoring ? null : undefined,
          fieldMonitoringFiles: isFieldMonitoring
            ? selectedMatricula?.reports.filter(r => r.id !== selectedFile?.id).map(r => ({ id: r.id }))
            : undefined
        }
      }
    })
      .then(() => {
        return removeFilesFromAgFinancing([selectedFile?.id || '']);
      })
      .then(() => {
        addFlashMessage({
          content: i18next.t('fileRemovedSuccessfully'),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={closeModal}
      title={i18next.t('deleteFile')}
      dialogMinHeight={'16rem'}
      dialogMaxHeight={'40%'}
    >
      <DialogContent>
        <span>
          {i18next.t('removeFileConfirmation')} <b>{selectedFile?.documentName}</b>?
        </span>
      </DialogContent>
      <ModalActionButtons
        closeModal={closeModal}
        changeButtonName={i18next.t('remove')}
        disabled={!selectedMatricula?.kml}
        save={deleteFile}
        destructive={true}
      />
    </EnhancedDialog>
  );
};
